import React, { useState, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { AuthContext } from '../../context/auth-context';
import ProjectEmptyState from '../../projects/ProjectEmptyState';
import AreasAPI from '../../../services/api/admin/areas-api';
import ProductionMetricsAPI from '../../../services/api/analytics/production-metrics-api';
import HeadcountTab from './tabs/HeadcountTab';
import CumulativeHoursTab from './tabs/CumulativeHoursTab';
import WorkPackagesTab from './tabs/WorkPackagesTab';
import KpiTab from './tabs/KpiTab';
import KpiTrendsTab from './tabs/KpiTrendsTab';
import DataQualityWarnings from './components/DataQualityWarnings';
import ProductionMetricsFilterBar from './components/ProductionMetricsFilterBar';
import ShopSyncExceptionChip from '../../shared/ShopSyncExceptionChip';

/**
 * Production Metrics Dashboard — shell.
 *
 * Header matches the app-wide pattern: project selector on the left,
 * page title + compact warning chip on the right.
 * Warnings are surfaced as a single chip in the header rather than
 * stacked alert rows inside each tab.
 */
const ProductionMetricsDashboard = () => {
  const auth = useContext(AuthContext);

  // Global project selection from Redux (set via the sidebar drawer).
  const selectedProject = useSelector((state) => state.project.selectedProject);
  const selectedProjectId = selectedProject?.id || '';

  const [activeTab, setActiveTab] = useState(0);
  const [warnings, setWarnings] = useState([]);

  // ── Filter states ───────────────────────────────────────────────────
  const [filterAreaName, setFilterAreaName] = useState(null);
  const [filterPmId, setFilterPmId] = useState(null);
  const [filterSearchText, setFilterSearchText] = useState('');
  const [areas, setAreas] = useState([]);
  const [packagesData, setPackagesData] = useState(null);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packagesError, setPackagesError] = useState(null);

  // Clear warnings and filters when project changes
  useEffect(() => {
    setWarnings([]);
    setFilterAreaName(null);
    setFilterPmId(null);
    setFilterSearchText('');
    setAreas([]);
    setPackagesData(null);
    setPackagesError(null);
  }, [selectedProjectId]);

  // Fetch project-level warnings for the header chip
  useEffect(() => {
    if (!selectedProjectId || !auth.token) return;
    let cancelled = false;

    const fetchWarnings = async () => {
      const [legacyResult, typedResult] = await Promise.all([
        ProductionMetricsAPI.getDataQualityWarnings(
          selectedProjectId,
          auth.token
        ).catch((err) => {
          console.error('Failed to load production metrics warnings:', err);
          return null;
        }),
        ProductionMetricsAPI.getDataQuality(
          selectedProjectId,
          auth.token
        ).catch((err) => {
          console.error(
            'Failed to load production metrics data quality checks:',
            err
          );
          return null;
        }),
      ]);

      if (cancelled) return;

      const legacyWarnings = Array.isArray(legacyResult?.dataQualityWarnings)
        ? legacyResult.dataQualityWarnings
        : [];

      const labels = {
            progress_before_installation_lane:
              'Progress entered but work package not in field installation lane',
            missing_actual_start:
              'In installation/prefab lane without actual start',
            complete_without_finish: 'Complete package missing actual finish',
            earned_drift: 'Earned hours drift from budget x percent',
            future_start_date: 'Actual start date is in the future',
            lane_eligibility: 'Package is in an ineligible workflow lane',
            past_anchor_incomplete:
              'Past installation/prefab lane but not recorded complete',
      };
      const typedWarnings = Object.entries(typedResult?.checks || {})
        .filter(([, rows]) => (rows || []).length > 0)
        .map(([checkName, rows]) => {
          const packages = [
            ...new Set(
              (rows || []).map((row) => {
                const name = row.autodesk_name || row.package_id;
                return row.detail ? `${name} — ${row.detail}` : name;
              })
            ),
          ];
          const count = packages.length;
          return {
            message: `${labels[checkName] || checkName} (${count} package${count !== 1 ? 's' : ''})`,
            packages,
          };
        });

      setWarnings([...legacyWarnings, ...typedWarnings]);
    };

    fetchWarnings();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, auth.token]);

  // Fetch leaf areas for the project
  useEffect(() => {
    if (!selectedProjectId || !auth.token) return;
    let cancelled = false;

    const fetchAreas = async () => {
      try {
        const list = await AreasAPI.getLeafAreas(selectedProjectId, auth.token);
        if (!cancelled) setAreas(list);
      } catch (err) {
        console.error('Failed to load project leaf areas:', err);
      }
    };

    fetchAreas();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, auth.token]);

  // Fetch work packages categories/data for the project
  useEffect(() => {
    if (!selectedProjectId || !auth.token) return;
    let cancelled = false;

    const fetchPackages = async () => {
      setLoadingPackages(true);
      setPackagesError(null);
      try {
        const result = await ProductionMetricsAPI.getPackages(
          selectedProjectId,
          auth.token
        );
        if (!cancelled) setPackagesData(result);
      } catch (err) {
        console.error('Failed to load work packages data:', err);
        if (!cancelled)
          setPackagesError(err.message || 'Failed to load packages');
      } finally {
        if (!cancelled) setLoadingPackages(false);
      }
    };

    fetchPackages();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, auth.token]);

  // Extract distinct project managers from packages
  const pmOptions = React.useMemo(() => {
    if (!packagesData || !packagesData.categories) return [];
    const seen = new Set();
    const pms = [];
    packagesData.categories.forEach((cat) => {
      if (cat.packages) {
        cat.packages.forEach((pkg) => {
          if (pkg.pmEmployeeId && !seen.has(pkg.pmEmployeeId)) {
            seen.add(pkg.pmEmployeeId);
            pms.push({
              id: pkg.pmEmployeeId,
              name: pkg.pmDisplayName || pkg.pmEmployeeId,
            });
          }
        });
      }
    });
    return pms.sort((a, b) => a.name.localeCompare(b.name));
  }, [packagesData]);

  if (!selectedProject) {
    return <ProjectEmptyState />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto',
        px: 3,
        py: 2,
      }}
    >
      {/* ── Header card: matches Analytics.js app-wide pattern ────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          bgcolor: '#fff',
          borderRadius: '10px',
          px: 2,
          py: 1.5,
          mb: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        {/* Filter controls */}
        <ProductionMetricsFilterBar
          projectId={selectedProjectId}
          areas={areas}
          pmOptions={pmOptions}
          filterAreaName={filterAreaName}
          setFilterAreaName={setFilterAreaName}
          filterPmId={filterPmId}
          setFilterPmId={setFilterPmId}
          filterSearchText={filterSearchText}
          setFilterSearchText={setFilterSearchText}
          onClearFilters={() => {
            setFilterAreaName(null);
            setFilterPmId(null);
            setFilterSearchText('');
          }}
        />

        {/* Right: page title */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}
        >
          Production Metrics
        </Typography>
      </Box>

      {/* ── Warning chips: below header, above tabs ──────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1,
          '&:empty': { display: 'none', mb: 0 },
        }}
      >
        <DataQualityWarnings warnings={warnings} />
        <ShopSyncExceptionChip
          projectId={selectedProjectId}
          projectName={selectedProject?.name || ''}
          token={auth.token}
          canResolve={!!auth.isAdmin}
        />
      </Box>

      {/* ── Tab bar ─────────────────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
          },
        }}
      >
        <Tab label="KPIs" />
        <Tab label="Work Package Progress" />
        <Tab label="Labor Headcount" />
        <Tab label="Cumulative Hours" />
        <Tab label="KPI Trends" />
      </Tabs>

      {/* ── Active tab content ──────────────────────────────────────── */}
      {activeTab === 0 && (
        <KpiTab
          projectId={selectedProjectId}
          filterAreaName={filterAreaName}
          filterPmId={filterPmId}
          filterSearchText={filterSearchText}
        />
      )}
      {activeTab === 1 && (
        <WorkPackagesTab
          projectId={selectedProjectId}
          packagesData={packagesData}
          loadingPackages={loadingPackages}
          packagesError={packagesError}
          filterAreaName={filterAreaName}
          filterPmId={filterPmId}
          filterSearchText={filterSearchText}
        />
      )}
      {activeTab === 2 && <HeadcountTab projectId={selectedProjectId} />}
      {activeTab === 3 && <CumulativeHoursTab projectId={selectedProjectId} />}
      {activeTab === 4 && <KpiTrendsTab projectId={selectedProjectId} />}
    </Box>
  );
};

export default ProductionMetricsDashboard;
